/**
 * Deployment Service
 * AIDLC Administrative Frontend
 *
 * API calls for the deployment pipeline and Azure KPI dashboard.
 * Consumes backend endpoints at /api/deployment and /api/azure-kpi.
 */

import apiClient from './api';
import type { DeploymentState, DeploymentRecord, LogEntry, AzureKpiData } from '../types/deployment';

const DEPLOY_PATH = '/deployment';
const KPI_PATH = '/azure-kpi';

// ─── Deployment Pipeline ──────────────────────────────────────────

/** Get current deployment pipeline status */
export async function getDeploymentStatus(): Promise<DeploymentState> {
  const response = await apiClient.get<DeploymentState>(`${DEPLOY_PATH}/status`);
  return response.data;
}

/** Get deployment history records */
export async function getDeploymentHistory(limit = 20): Promise<DeploymentRecord[]> {
  const response = await apiClient.get<DeploymentRecord[]>(`${DEPLOY_PATH}/history`, {
    params: { limit },
  });
  return response.data;
}

/** Get build/deploy logs, optionally for a specific deployment */
export async function getDeploymentLogs(deploymentId?: string): Promise<LogEntry[]> {
  const params = deploymentId ? { deploymentId } : {};
  const response = await apiClient.get<LogEntry[]>(`${DEPLOY_PATH}/logs`, { params });
  return response.data;
}

/** Trigger a new build (compile frontend + backend + package) */
export async function triggerBuild(): Promise<{ deploymentId: string }> {
  const response = await apiClient.post<{ deploymentId: string }>(`${DEPLOY_PATH}/build`);
  return response.data;
}

/** Deploy the built package to Azure (requires a successful build) */
export async function triggerDeploy(): Promise<{ deploymentId: string }> {
  const response = await apiClient.post<{ deploymentId: string }>(`${DEPLOY_PATH}/deploy`);
  return response.data;
}

/** Cancel the currently running build or deployment */
export async function cancelDeployment(): Promise<void> {
  await apiClient.post(`${DEPLOY_PATH}/cancel`);
}

// ─── Azure KPIs ───────────────────────────────────────────────────

/** Get all Azure KPI data in a single call */
export async function getAzureKpis(): Promise<AzureKpiData> {
  const response = await apiClient.get<AzureKpiData>(KPI_PATH, {
    timeout: 35000, // Azure metrics can be slow
  });
  return response.data;
}

/** Get Azure App Service health only */
export async function getAzureHealth(): Promise<AzureKpiData['serviceHealth']> {
  const response = await apiClient.get<AzureKpiData['serviceHealth']>(`${KPI_PATH}/health`);
  return response.data;
}

/** Get Azure performance metrics only */
export async function getAzurePerf(): Promise<AzureKpiData['performanceMetrics']> {
  const response = await apiClient.get<AzureKpiData['performanceMetrics']>(`${KPI_PATH}/perf`);
  return response.data;
}

/** Get Azure cost data only */
export async function getAzureCost(): Promise<AzureKpiData['costData']> {
  const response = await apiClient.get<AzureKpiData['costData']>(`${KPI_PATH}/cost`);
  return response.data;
}
